import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { endpoints } from '@/services/api';
import type { DashboardLayoutPreset } from '@/store/slices/layoutPresetsSlice';
import type { DashboardGridItem } from '@/store/slices/dashboardEditSlice';

const getApiBaseUrl = (): string => {
  // Priority 1: explicit env var (production builds, Cloud Run)
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.trim() !== '') {
    return import.meta.env.VITE_API_URL;
  }
  // Priority 2: relative URL — routes through Vite dev proxy (/api → http://backend:3000)
  // The Vite proxy in vite.config.ts already handles /api → http://backend:3000.
  // This makes the request same-origin (no CORS) regardless of frontend hostname
  // (localhost, Tailscale, LAN — all work without backend CORS config changes).
  return '/api/v1';
};

interface WrappedResponse<T> {
  data?: T;
}

interface SavePresetBody {
  name: string;
  description?: string;
  layout?: DashboardGridItem[];
}

interface SavePresetArgs {
  dashboardId: string;
  body: SavePresetBody;
}

interface ApplyPresetArgs {
  dashboardId: string;
  presetId: string;
}

interface DeletePresetArgs {
  dashboardId: string;
  presetId: string;
}

interface RenamePresetArgs {
  dashboardId: string;
  presetId: string;
  body: {
    name: string;
    description?: string;
  };
}

export const dashboardLayoutPresetsApi = createApi({
  reducerPath: 'dashboardLayoutPresetsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    credentials: 'include',
  }),
  tagTypes: ['DashboardLayoutPreset', 'Dashboard'],
  endpoints: (builder) => ({
    getLayoutPresets: builder.query<DashboardLayoutPreset[], string>({
      query: (dashboardId) => endpoints.dashboards.layoutPresets(dashboardId),
      transformResponse: (response: WrappedResponse<DashboardLayoutPreset[]> | DashboardLayoutPreset[]) =>
        Array.isArray(response) ? response : (response.data ?? []),
      providesTags: (_result, _error, dashboardId) => [
        { type: 'DashboardLayoutPreset', id: dashboardId },
      ],
    }),
    saveLayoutPreset: builder.mutation<DashboardLayoutPreset, SavePresetArgs>({
      query: ({ dashboardId, body }) => ({
        url: endpoints.dashboards.layoutPresets(dashboardId),
        method: 'POST',
        body,
      }),
      transformResponse: (response: WrappedResponse<DashboardLayoutPreset> | DashboardLayoutPreset) =>
        'data' in (response as WrappedResponse<DashboardLayoutPreset>)
          ? (response as WrappedResponse<DashboardLayoutPreset>).data as DashboardLayoutPreset
          : (response as DashboardLayoutPreset),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DashboardLayoutPreset', id: arg.dashboardId },
      ],
    }),
    applyLayoutPreset: builder.mutation<unknown, ApplyPresetArgs>({
      query: ({ dashboardId, presetId }) => ({
        url: endpoints.dashboards.applyLayoutPreset(dashboardId, presetId),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Dashboard', id: arg.dashboardId },
        { type: 'DashboardLayoutPreset', id: arg.dashboardId },
      ],
    }),
    resetDashboardLayout: builder.mutation<unknown, string>({
      query: (dashboardId) => ({
        url: endpoints.dashboards.layoutReset(dashboardId),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, dashboardId) => [
        { type: 'Dashboard', id: dashboardId },
      ],
    }),
    deleteLayoutPreset: builder.mutation<unknown, DeletePresetArgs>({
      query: ({ dashboardId, presetId }) => ({
        url: endpoints.dashboards.layoutPresetById(dashboardId, presetId),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DashboardLayoutPreset', id: arg.dashboardId },
      ],
    }),
    renameLayoutPreset: builder.mutation<unknown, RenamePresetArgs>({
      query: ({ dashboardId, presetId, body }) => ({
        url: endpoints.dashboards.layoutPresetById(dashboardId, presetId),
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DashboardLayoutPreset', id: arg.dashboardId },
      ],
    }),
  }),
});

export const {
  useGetLayoutPresetsQuery,
  useSaveLayoutPresetMutation,
  useApplyLayoutPresetMutation,
  useResetDashboardLayoutMutation,
  useDeleteLayoutPresetMutation,
  useRenameLayoutPresetMutation,
} = dashboardLayoutPresetsApi;
